import { useEffect, useRef } from 'react';
import { toast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';
// Static-imported (was previously `await import(...)`) so the Convert
// button doesn't hit the "Failed to fetch dynamically imported module"
// stale-chunk failure mode after a deploy. The supabase client module
// is in the main bundle anyway (every component imports it), so this
// is purely the same chunk-stale-risk-removal fix applied to
// pickCompatibleBayForTech / catalogBayRules earlier this week.
import { supabase } from '@/integrations/supabase/client';
import { notifyDayboardInvalidated } from '@/utils/dayboardInvalidation';
import { useAuth } from '@/contexts/AuthContext';

interface EODBufferData {
  ticketId: string;
  customerName: string;
  techName: string;
  techEOD: string;
  rolloverMinutes: number;
  /** When 'Waiter', the toast hides the "Convert to Rollover" button —
   *  rolling a Waiter breaks the customer's promised pickup. The server-
   *  side createEODRolloverSplit also refuses Waiters as defense-in-depth. */
  ticketType?: string | null;
}

export function EODBufferToast() {
  const warnedTickets = useRef<Set<string>>(new Set());
  const { activeShop } = useAuth();

  useEffect(() => {
    const handleBufferWarning = (event: CustomEvent<EODBufferData>) => {
      const data = event.detail;
      
      // Only show toast once per ticket
      if (warnedTickets.current.has(data.ticketId)) {
        return;
      }
      
      warnedTickets.current.add(data.ticketId);

      const handleReassign = () => {
        // The Reassign button previously dispatched `'trigger-reassignment'`,
        // an event with no listener anywhere in the codebase (grep
        // confirms zero `addEventListener('trigger-reassignment'...)`).
        // The result was a dead button: clicking did nothing visible.
        // Until the cross-component plumbing for an in-toast reassign
        // flow is wired up (e.g., open OptiPlaceModal in reassign-mode
        // for this ticket), give the operator an honest, actionable
        // guidance toast so the click is never silent.
        toast({
          title: 'Reassign in OptiBoard',
          description: `Open OptiBoard or Birdseye and drag ${data.customerName}'s ticket to a different bay or technician to reassign.`,
          duration: 8000,
        });
      };

      const handleConvert = async () => {
        try {
          // A ticket that already continues has its future carried: never a
          // second carry (the server refuses it with ROLLOVER_SUCCESSOR_EXISTS).
          const { liveRolloverSuccessorId } = await import('@/utils/rolloverChain');
          if (await liveRolloverSuccessorId(data.ticketId)) {
            toast({
              title: 'Already carried',
              description: `${data.customerName}'s ticket already has a rollover continuation — its remaining work is already scheduled.`,
            });
            return;
          }
          const { data: result, error } = await supabase.functions.invoke('create-eod-rollover', {
            body: { ticketId: data.ticketId },
          });

          if (error || !result?.success) throw new Error(result?.error || error?.message || 'Failed');

          toast({
            title: 'Converted to Rollover',
            description: `Ticket for ${data.customerName} has been converted.`,
          });

          void notifyDayboardInvalidated({
            reason: 'eod-convert',
            shopId: activeShop?.id || '',
            ticketId: data.ticketId,
          });
        } catch (error: any) {
          // Previously the catch only console.error'd — operator saw the
          // toast disappear with no result, indistinguishable from a
          // successful click. Surface the failure so the click is never
          // silent. `create-eod-rollover` is deployed, so failures here
          // are most likely auth / RLS / row-state issues that the
          // operator needs to see (e.g., "Ticket not found" / "Waiter
          // ticket cannot be rolled over").
          //
          // Fallback copy: supabase.functions.invoke() on a 4xx/5xx
          // surfaces `error.message` as the generic "Edge Function
          // returned a non-2xx status code"; the actual server reason
          // lives in `error.context.response` which we don't currently
          // read. The most common reachable failure from THIS toast
          // (the Waiter case is already hidden via the `!isWaiter`
          // guard above) is `createEODRolloverSplit` refusing the
          // ticket because it isn't running or paused-in-bay. The
          // fallback description names that cause and the operator's
          // next-step options.
          console.error('Error converting to rollover:', error);
          toast({
            title: 'Convert to Rollover Failed',
            description:
              'Only running or paused-in-bay tickets can be converted to Rollover. Start the timer on this ticket first, or reassign instead.',
            variant: 'destructive',
            duration: 8000,
          });
        }
      };

      const isWaiter = data.ticketType === 'Waiter';

      toast({
        title: 'Approaching EOD Buffer',
        description: (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-yellow-500">
              <AlertTriangle className="h-4 w-4" />
              <p className="text-sm font-medium">EOD Buffer Zone</p>
            </div>
            <p className="text-sm">
              Ticket for <strong>{data.customerName}</strong> with{' '}
              <strong>{data.techName}</strong> will extend {data.rolloverMinutes} minutes past EOD ({data.techEOD}).
            </p>
            {isWaiter && (
              <p className="text-xs text-destructive font-medium">
                ⚠️ Waiter customer is in the lobby — rolling to tomorrow
                would break the promise. Reassign or convert to Drop-off.
              </p>
            )}
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleReassign}
                className="text-xs"
              >
                Reassign
              </Button>
              {!isWaiter && (
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleConvert}
                  className="text-xs"
                >
                  Convert to Rollover
                </Button>
              )}
            </div>
          </div>
        ),
        duration: 10000,
      });
    };

    window.addEventListener('eod-buffer-warning', handleBufferWarning as EventListener);
    
    return () => {
      window.removeEventListener('eod-buffer-warning', handleBufferWarning as EventListener);
    };
  }, []);

  return null;
}
